import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { TrendingUp, Activity, Clock, BarChart3, Loader2 } from "lucide-react";
import BuyViaDhan from "@/components/BuyViaDhan";

/* Options Lab Sprint 3 — the four live quadrants:
   OI Build-up map · Strike-wise Greeks + IV Rank · FII/DII flow · Pre-market cues */

const TAG_META = {
  long_buildup:   { s: "LB", cls: "text-[#22C55E]", title: "Long Buildup (price↑ OI↑)" },
  short_buildup:  { s: "SB", cls: "text-[#EF4444]", title: "Short Buildup (price↓ OI↑)" },
  long_unwinding: { s: "LU", cls: "text-[#F59E0B]", title: "Long Unwinding (price↓ OI↓)" },
  short_covering: { s: "SC", cls: "text-[#3B9BFF]", title: "Short Covering (price↑ OI↓)" },
  neutral:        { s: "—", cls: "text-zinc-600", title: "No change" },
};

function fmtC(n) {
  if (n === null || n === undefined) return "—";
  const a = Math.abs(n);
  if (a >= 1e7) return `${(n / 1e7).toFixed(1)}Cr`;
  if (a >= 1e5) return `${(n / 1e5).toFixed(1)}L`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return String(n);
}

function Card({ title, subtitle, icon: Icon, children, verdict, testid }) {
  const vCls = verdict === "bullish" ? "text-[#22C55E] border-[#22C55E]/40" :
               verdict === "bearish" ? "text-[#EF4444] border-[#EF4444]/40" : "text-zinc-400 border-zinc-700";
  return (
    <div className="border border-zinc-800 bg-[#0a0a0a] p-4 min-h-[260px] flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Icon className="h-3.5 w-3.5 text-zinc-500" />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{title}</span>
          </div>
          <div className="font-heading text-base font-bold uppercase tracking-tight">{subtitle}</div>
        </div>
        {verdict && <span className={`text-[10px] uppercase tracking-widest font-mono font-bold border px-2 py-0.5 ${vCls}`}>{verdict}</span>}
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="h-full flex items-center justify-center text-[10px] font-mono uppercase tracking-widest text-zinc-600 text-center px-4">{msg}</div>;
}

function useFetch(path, deps) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(path);
      setData(d); setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [path]);
  useEffect(() => { load(); const id = setInterval(load, 120_000); return () => clearInterval(id); }, deps); // eslint-disable-line
  return { data, err, loading };
}

export function OIBuildupCard({ symbol, expiry }) {
  const { data, err, loading } = useFetch(`/options-lab/oi-buildup?symbol=${symbol}&expiry=${expiry}`, [symbol, expiry]);
  return (
    <Card title="OI Build-up" subtitle="Long Buildup / Short Cover map" icon={TrendingUp}
          verdict={data?.verdict} testid="oi-buildup-card">
      {loading && <Empty msg={<Loader2 className="h-4 w-4 animate-spin inline" />} />}
      {err && !loading && <Empty msg={err} />}
      {data && !loading && (
        <div className="space-y-2">
          <div className="flex gap-2 text-[9px] font-mono uppercase tracking-widest">
            <div className="flex-1 border border-[#EF4444]/30 bg-[#EF4444]/5 p-1.5">
              <div className="text-zinc-500">Call walls (resistance)</div>
              <div className="text-[#fca5a5]">{data.walls_resistance.map((w) => `${w.strike}`).join(" · ") || "—"}</div>
            </div>
            <div className="flex-1 border border-[#22C55E]/30 bg-[#22C55E]/5 p-1.5">
              <div className="text-zinc-500">Put walls (support)</div>
              <div className="text-[#86efac]">{data.walls_support.map((w) => `${w.strike}`).join(" · ") || "—"}</div>
            </div>
          </div>
          <div className="max-h-44 overflow-auto">
            <table className="w-full text-[10px] font-mono">
              <thead className="text-zinc-600 uppercase sticky top-0 bg-[#0a0a0a]">
                <tr><th className="text-left py-1">CE tag</th><th className="text-right">CE ΔOI</th>
                    <th className="text-center px-2">Strike</th>
                    <th className="text-left">PE ΔOI</th><th className="text-right py-1">PE tag</th></tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const ce = TAG_META[r.ce?.tag || "neutral"], pe = TAG_META[r.pe?.tag || "neutral"];
                  return (
                    <tr key={r.strike} className={r.is_atm ? "bg-[#007AFF]/10" : ""}>
                      <td className={`py-0.5 ${ce.cls}`} title={ce.title}>{ce.s}</td>
                      <td className="text-right text-zinc-400">{fmtC(r.ce?.oich)}</td>
                      <td className={`text-center px-2 ${r.is_atm ? "text-[#3B9BFF] font-bold" : "text-zinc-300"}`}>{r.strike}</td>
                      <td className="text-zinc-400">{fmtC(r.pe?.oich)}</td>
                      <td className={`text-right py-0.5 ${pe.cls}`} title={pe.title}>{pe.s}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest">
            LB long buildup · SB short buildup · LU long unwind · SC short cover
          </div>
        </div>
      )}
    </Card>
  );
}

export function GreeksCard({ symbol, expiry }) {
  const { data, err, loading } = useFetch(`/options-lab/greeks?symbol=${symbol}&expiry=${expiry}`, [symbol, expiry]);
  const ivr = data?.iv_rank;
  return (
    <Card title="Strike-wise Greeks" subtitle="Δ · Θ · Vega · IV Rank" icon={BarChart3}
          verdict={ivr?.label ? `IVR ${ivr.rank} · ${ivr.label}` : null} testid="greeks-card">
      {loading && <Empty msg={<Loader2 className="h-4 w-4 animate-spin inline" />} />}
      {err && !loading && <Empty msg={err} />}
      {data && !loading && (
        <div className="space-y-2">
          {ivr && ivr.rank === null && (
            <div className="text-[9px] font-mono uppercase tracking-widest text-[#F59E0B]">{ivr.note}</div>
          )}
          <div className="max-h-48 overflow-auto">
            <table className="w-full text-[10px] font-mono">
              <thead className="text-zinc-600 uppercase sticky top-0 bg-[#0a0a0a]">
                <tr><th className="text-left py-1">CE Δ/Θ</th><th className="text-right">CE LTP</th>
                    <th className="text-center px-2">Strike</th>
                    <th className="text-left">PE LTP</th><th className="text-right py-1">PE Δ/Θ</th></tr>
              </thead>
              <tbody>
                {(data.table || []).map((r) => (
                  <tr key={r.strike} className={r.is_atm ? "bg-[#007AFF]/10" : ""}>
                    <td className="py-0.5 text-zinc-300">{r.ce ? `${r.ce.delta ?? "—"} / ${r.ce.theta ?? "—"}` : "—"}</td>
                    <td className="text-right text-zinc-400">{r.ce?.ltp ?? "—"}</td>
                    <td className={`text-center px-2 ${r.is_atm ? "text-[#3B9BFF] font-bold" : "text-zinc-300"}`}>{r.strike}</td>
                    <td className="text-zinc-400">{r.pe?.ltp ?? "—"}</td>
                    <td className="text-right py-0.5 text-zinc-300">{r.pe ? `${r.pe.delta ?? "—"} / ${r.pe.theta ?? "—"}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest">
            Θ = decay/day per unit · ATM IV {data.atm_iv ? `${data.atm_iv.toFixed(1)}%` : "—"} · DTE {data.dte ?? "—"}
          </div>
        </div>
      )}
    </Card>
  );
}

export function FiiDiiCard() {
  const { data, err, loading } = useFetch("/options-lab/fii-dii", []);
  return (
    <Card title="FII / DII Derivatives" subtitle="5-session net flow" icon={Activity}
          verdict={data?.verdict} testid="fii-dii-card">
      {loading && <Empty msg={<Loader2 className="h-4 w-4 animate-spin inline" />} />}
      {err && !loading && <Empty msg={err} />}
      {data && !loading && (
        data.sessions?.length ? (
          <div className="space-y-1">
            <div className="grid grid-cols-4 text-[9px] font-mono uppercase tracking-widest text-zinc-600 pb-1 border-b border-zinc-800">
              <span>Session</span><span className="text-right">FII Fut Net</span>
              <span className="text-right">FII CE Net</span><span className="text-right">FII PE Net</span>
            </div>
            {data.sessions.map((sx) => (
              <div key={sx.day} className="grid grid-cols-4 text-[10px] font-mono py-0.5">
                <span className="text-zinc-400">{sx.day.slice(5)}</span>
                <span className={`text-right ${(sx.FII?.fut_idx_net || 0) >= 0 ? "text-[#86efac]" : "text-[#fca5a5]"}`}>{fmtC(sx.FII?.fut_idx_net)}</span>
                <span className="text-right text-zinc-300">{fmtC(sx.FII?.opt_idx_call_net)}</span>
                <span className="text-right text-zinc-300">{fmtC(sx.FII?.opt_idx_put_net)}</span>
              </div>
            ))}
            <div className="text-[9px] font-mono text-zinc-600 uppercase tracking-widest pt-1">
              Net contracts (long − short) · NSE participant-wise OI · EOD
            </div>
          </div>
        ) : <Empty msg={data.note || "No sessions available"} />
      )}
    </Card>
  );
}

/** Strike selection panel for the Options Lab — same intelligence as the Dashboard
 *  Best-Strike strip. Auto-syncs with the symbol's active signal when one exists,
 *  otherwise lets the user pick a direction manually. */
export function StrikeSelectCard({ symbol }) {
  const [direction, setDirection] = useState("bullish");
  const [signal, setSignal] = useState(null);       // active tracked trade for symbol
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let alive = true;
    api.get("/signals/recent-opens?minutes=360")
      .then(({ data: d }) => {
        if (!alive) return;
        const m = (d.opens || []).find((o) => o.symbol === symbol);
        setSignal(m || null);
        if (m?.trend) setDirection(m.trend);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [symbol]);

  useEffect(() => {
    let alive = true;
    let attempts = 0;
    setState("loading");
    const p = new URLSearchParams({ symbol, direction });
    if (signal && signal.trend === direction) {
      if (signal.entry) p.set("entry", signal.entry);
      if (signal.stop_loss) p.set("stop_loss", signal.stop_loss);
      if (signal.target_1) p.set("target_1", signal.target_1);
      if (signal.target_2) p.set("target", signal.target_2);
    }
    const fetchIt = async () => {
      attempts += 1;
      try {
        const { data: d } = await api.get(`/options-lab/strike-suggest?${p.toString()}`);
        if (!alive) return;
        if (d?.ok) { setData(d); setState("ok"); return; }
        throw new Error("unavailable");
      } catch {
        if (!alive) return;
        if (attempts < 3) setTimeout(fetchIt, 5000);
        else setState("error");
      }
    };
    fetchIt();
    return () => { alive = false; };
  }, [symbol, direction, signal]);

  const b = data?.best;
  const plan = data?.trade_plan;
  const ivr = data?.iv_rank || {};
  const ivrTone = ivr.label === "cheap" ? "text-[#22C55E]" : ivr.label === "expensive" ? "text-[#EF4444]" : "text-zinc-300";
  return (
    <div className="border border-[#007AFF]/30 bg-[#007AFF]/5 p-4" data-testid="strike-select-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#3B9BFF] font-mono">Strike Selection · {symbol}</div>
          <div className="font-heading text-base font-bold uppercase tracking-tight">
            Best Strike & Premium Plan {data?.expiry ? `· ${data.expiry}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {signal && (
            <span className="text-[9px] font-mono uppercase tracking-widest text-[#22C55E] border border-[#22C55E]/40 px-2 py-1" data-testid="strike-signal-synced">
              ● Synced to live signal
            </span>
          )}
          <div className="flex border border-zinc-700">
            {["bullish", "bearish"].map((d) => (
              <button key={d} onClick={() => setDirection(d)} data-testid={`strike-dir-${d}`}
                      className={`px-3 py-1 text-[10px] uppercase tracking-widest font-mono font-bold ${direction === d
                        ? d === "bullish" ? "bg-[#22C55E] text-black" : "bg-[#EF4444] text-black"
                        : "text-zinc-400 hover:text-white"}`}>
                {d === "bullish" ? "CALLS" : "PUTS"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!b && (
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 py-4 text-center">
          {state === "error" ? "Option chain busy — auto-retrying shortly" : "Fetching best strike…"}
        </div>
      )}
      {b && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-heading font-black text-2xl text-white" data-testid="strike-select-pick">
              {b.strike} {b.type}
            </span>
            <span className="font-mono text-sm text-zinc-300">LTP ₹{b.ltp}</span>
            <span className="font-mono text-xs text-zinc-400">Δ {b.delta} · Θ {b.theta}/d · Vega {b.vega} · IV {b.iv?.toFixed ? b.iv.toFixed(1) : b.iv}%</span>
            {ivr.rank != null
              ? <span className={`font-mono text-xs ${ivrTone}`}>IVR {ivr.rank} · {ivr.label}</span>
              : <span className="font-mono text-[10px] text-zinc-500 uppercase">{ivr.note}</span>}
          </div>
          {plan ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[["Entry" + (plan.mode === "limit_order" ? " (limit)" : ""), plan.limit_entry, "text-[#3B9BFF]"],
                ["SL premium", plan.sl_premium, "text-[#EF4444]"],
                ["T1 premium", plan.t1_premium, "text-[#22C55E]"],
                [`T2 premium${plan.premium_rr ? ` (RR ${plan.premium_rr})` : ""}`, plan.t2_premium, "text-[#22C55E]"]]
                .filter(([, v]) => v != null)
                .map(([l, v, cls]) => (
                  <div key={l} className="border border-zinc-800 bg-[#0a0a0a] p-2">
                    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">{l}</div>
                    <div className={`font-heading font-black text-lg ${cls}`}>₹{v}</div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
              No active signal for {symbol} — showing best strike only. Premium plan appears when a signal is live.
            </div>
          )}
          {plan?.note && <div className={`text-[10px] leading-snug ${plan.mode === "limit_order" ? "text-[#F59E0B]" : "text-zinc-500"}`}>{plan.note}</div>}
          {data.wall_warning && <div className="text-[10px] text-[#F59E0B] leading-snug">⚠ {data.wall_warning}</div>}
          <BuyViaDhan symbol={symbol} strike={b.strike} optType={b.type} expiry={data.expiry}
                      suggestedPrice={plan?.limit_entry ?? b.ltp} />
        </div>
      )}
    </div>
  );
}

export function PremarketCard() {
  const { data, err, loading } = useFetch("/options-lab/premarket", []);
  const bias = data?.bias === "gap_up" ? "bullish" : data?.bias === "gap_down" ? "bearish" : data?.bias ? "neutral" : null;
  return (
    <Card title="Pre-Market Cues" subtitle="Global proxies · Implied open" icon={Clock}
          verdict={bias} testid="premarket-card">
      {loading && <Empty msg={<Loader2 className="h-4 w-4 animate-spin inline" />} />}
      {err && !loading && <Empty msg={err} />}
      {data && !loading && (
        <div className="space-y-2">
          <div className="border border-zinc-800 bg-[#121212] p-2 flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Implied India open</span>
            <span className={`font-heading font-black text-xl ${(data.implied_open_pct || 0) > 0.25 ? "text-[#22C55E]" : (data.implied_open_pct || 0) < -0.25 ? "text-[#EF4444]" : "text-zinc-300"}`}>
              {data.implied_open_pct != null ? `${data.implied_open_pct > 0 ? "+" : ""}${data.implied_open_pct}%` : "—"}
            </span>
          </div>
          <div className="space-y-0.5">
            {data.cues.map((c) => (
              <div key={c.symbol} className="flex items-center justify-between text-[10px] font-mono py-0.5">
                <span className="text-zinc-400 uppercase tracking-wide">{c.label}</span>
                <span className={c.chg_pct == null ? "text-zinc-600" : c.chg_pct >= 0 ? "text-[#86efac]" : "text-[#fca5a5]"}>
                  {c.chg_pct == null ? "n/a" : `${c.chg_pct > 0 ? "+" : ""}${c.chg_pct}%`}
                </span>
              </div>
            ))}
          </div>
          <div className="text-[9px] font-mono text-zinc-600 leading-snug">{data.note}</div>
        </div>
      )}
    </Card>
  );
}
